import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import { Play, Target, CheckCircle, Monitor, Smartphone, ArrowRight } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';

const GAME_STATES = {
  READY: 'ready',
  DEVICE_SELECT: 'device_select',
  PLAYING: 'playing',
  FINISHED: 'finished',
};

// Device Mode Selection Component
const DeviceModeSelector = ({ onSelect }) => (
  <Card className="border-slate-200">
    <CardContent className="pt-8">
      <div className="text-center mb-6">
        <h2 className="font-heading text-xl font-bold text-[#0B1D51] mb-2">
          Select Your Device
        </h2>
        <p className="text-sm text-[#64748B]">
          Choose how you're taking this test for the best experience
        </p>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <Button
          variant="outline"
          className="h-32 flex-col gap-3 hover:border-[#0B1D51] hover:bg-[#0B1D51]/5"
          onClick={() => onSelect('desktop')}
          data-testid="select-desktop"
        >
          <Monitor className="w-10 h-10 text-[#0B1D51]" />
          <div>
            <p className="font-semibold">Laptop/Desktop</p>
            <p className="text-xs text-[#64748B]">Larger grid</p>
          </div>
        </Button>

        <Button
          variant="outline"
          className="h-32 flex-col gap-3 hover:border-[#FF9F1C] hover:bg-[#FF9F1C]/5"
          onClick={() => onSelect('mobile')}
          data-testid="select-mobile"
        >
          <Smartphone className="w-10 h-10 text-[#FF9F1C]" />
          <div>
            <p className="font-semibold">Mobile/Tablet</p>
            <p className="text-xs text-[#64748B]">Touch optimised</p>
          </div>
        </Button>
      </div>
    </CardContent>
  </Card>
);

// Dot pattern component - randomised layouts with fixed dot size and stable rotation
const DotPattern = ({ count, size = 'normal', seed = 1 }) => {
  const dotSize = size === 'small' ? 'w-1.5 h-1.5' : 'w-2 h-2';
  const gap = size === 'small' ? 'gap-0.5' : 'gap-1';

  // Deterministic random number generator.
  // Keeps pattern stable between re-renders.
  const seededRandom = (value) => {
    const x = Math.sin(value * 9999) * 10000;
    return x - Math.floor(x);
  };

  const pick = (items, offset = 0) => {
    const index = Math.floor(seededRandom(seed + count * 37 + offset) * items.length);
    return items[index];
  };

  // Different internal layout areas - filtered to ensure they can hold all dots
  // Each layout must have at least 'count' positions
  const allLayoutOptions = [
    { cols: 2, rows: 2 },  // 4 positions
    { cols: 3, rows: 2 },  // 6 positions
    { cols: 2, rows: 3 },  // 6 positions
    { cols: 3, rows: 3 },  // 9 positions
    { cols: 4, rows: 2 },  // 8 positions
    { cols: 2, rows: 4 },  // 8 positions
    { cols: 4, rows: 3 },  // 12 positions
    { cols: 3, rows: 4 },  // 12 positions
    { cols: 4, rows: 4 },  // 16 positions
  ];

  // Filter layouts to only include those that can hold all dots
  const validLayoutOptions = allLayoutOptions.filter(
    layout => layout.cols * layout.rows >= count
  );
  
  // Fallback to a safe layout if none are valid (shouldn't happen with count <= 5)
  const layoutOptions = validLayoutOptions.length > 0 ? validLayoutOptions : [{ cols: 3, rows: 3 }];

  const layout = pick(layoutOptions, 11);

  const totalPositions = layout.cols * layout.rows;
  const positions = Array.from({ length: totalPositions }, (_, index) => index);

  // Shuffle positions deterministically.
  const shuffledPositions = [...positions].sort((a, b) => {
    const randomA = seededRandom(seed + a * 17 + count * 31);
    const randomB = seededRandom(seed + b * 17 + count * 31);
    return randomA - randomB;
  });

  // Select active dot positions based on count.
  const activePositions = new Set(shuffledPositions.slice(0, count));

  // Rotate the whole dot group slightly.
  // This adds variation without changing dot size.
  const rotationOptions = [-12, -9, -6, -3, 0, 3, 6, 9, 12];
  const rotation = pick(rotationOptions, 41);

  return (
    <div
      className={`grid ${gap} items-center justify-center`}
      style={{
        gridTemplateColumns: `repeat(${layout.cols}, minmax(0, 1fr))`,
        gridTemplateRows: `repeat(${layout.rows}, minmax(0, 1fr))`,
        transform: `rotate(${rotation}deg)`,
        transformOrigin: 'center',
      }}
    >
      {positions.map((position) => {
        const isActive = activePositions.has(position);

        return (
          <div
            key={position}
            className={`${dotSize} rounded-full ${
              isActive ? 'bg-current' : 'bg-transparent'
            }`}
          />
        );
      })}
    </div>
  );
};

const SelectiveAttentionGame = ({ level, onComplete }) => {
  const [gameState, setGameState] = useState(GAME_STATES.READY);
  const [deviceMode, setDeviceMode] = useState(null);
  const [score, setScore] = useState(0);
  const [currentSection, setCurrentSection] = useState(0);
  const [timeLeft, setTimeLeft] = useState(120);
  const [grid, setGrid] = useState([]);
  const [selectedCells, setSelectedCells] = useState(new Set());
  const [feedback, setFeedback] = useState(null);
  const [sectionResults, setSectionResults] = useState([]);

  const startTime = useRef(Date.now());
  // Always points at the latest submitSection so the countdown timer's auto-submit
  // uses the user's current selections (not a stale closure from section start).
  const submitSectionRef = useRef(null);
  // Guards against the same section being scored twice (manual submit + timer firing).
  const sectionSubmittedRef = useRef(false);

  // Level-based configuration
  const levelConfig = {
    1: { targetDots: 4, timePerSection: 120, targetPercentage: 0.30 },
    2: { targetDots: 4, timePerSection: 100, targetPercentage: 0.27 },
    3: { targetDots: 4, timePerSection: 90, targetPercentage: 0.25 },
    4: { targetDots: 4, timePerSection: 80, targetPercentage: 0.22 },
    5: { targetDots: 4, timePerSection: 70, targetPercentage: 0.20 },
  };

  const config = levelConfig[level] || levelConfig[1];
  const TARGET_DOTS = config.targetDots;
  const TOTAL_SECTIONS = Math.min(3 + level, 5);

  // Generate grid of dot patterns
  const generateGrid = useCallback(() => {
    const rows = deviceMode === 'mobile' ? 8 : 10;
    const cols = deviceMode === 'mobile' ? 6 : 10;
    const cells = [];

    for (let i = 0; i < rows * cols; i++) {
      // Target percentage controls how likely a cell is to contain 4 dots.
      const isTarget = Math.random() < config.targetPercentage;

      const dotCount = isTarget
        ? TARGET_DOTS
        : [1, 2, 3, 5][Math.floor(Math.random() * 4)];

      cells.push({
        id: i,
        dotCount,
        isTarget: dotCount === TARGET_DOTS,
        seed: Date.now() + currentSection * 1000 + i * 97,
      });
    }

    return cells;
  }, [deviceMode, config.targetPercentage, TARGET_DOTS, currentSection]);

  // Reset time when game starts or section changes
  useEffect(() => {
    if (gameState === GAME_STATES.PLAYING) {
      setTimeLeft(config.timePerSection);
    }
  }, [gameState, currentSection, config.timePerSection]);

  // Timer countdown
  useEffect(() => {
    if (gameState !== GAME_STATES.PLAYING) return;

    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          if (submitSectionRef.current) submitSectionRef.current();
          return 0;
        }

        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
    // submitSection is intentionally not included here to avoid resetting the interval every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameState, currentSection]);

  // Initialise grid when playing
  useEffect(() => {
    if (gameState === GAME_STATES.PLAYING && grid.length === 0) {
      setGrid(generateGrid());
    }
  }, [gameState, grid.length, generateGrid]);

  const handleCellClick = (cell) => {
    if (gameState !== GAME_STATES.PLAYING) return;

    const newSelected = new Set(selectedCells);

    if (newSelected.has(cell.id)) {
      newSelected.delete(cell.id);
    } else {
      newSelected.add(cell.id);
    }

    setSelectedCells(newSelected);
  };

  const submitSection = () => {
    if (gameState !== GAME_STATES.PLAYING || grid.length === 0) return;
    // Prevent the same section being counted twice (e.g. manual submit then timer fires).
    if (sectionSubmittedRef.current) return;
    sectionSubmittedRef.current = true;

    let correct = 0;
    let wrong = 0;
    let missed = 0;

    grid.forEach((cell) => {
      const wasSelected = selectedCells.has(cell.id);

      if (cell.isTarget && wasSelected) {
        correct++;
      } else if (cell.isTarget && !wasSelected) {
        missed++;
      } else if (!cell.isTarget && wasSelected) {
        wrong++;
      }
    });

    const totalTargets = grid.filter((cell) => cell.isTarget).length;
    const sectionScore = Math.max(0, correct * 10 - wrong * 5);
    const accuracy = totalTargets > 0 ? (correct / totalTargets) * 100 : 0;

    const result = {
      section: currentSection + 1,
      correct,
      wrong,
      missed,
      totalTargets,
      score: sectionScore,
      accuracy,
    };

    setSectionResults((prev) => [...prev, result]);
    setScore((prevScore) => prevScore + sectionScore);

    if (wrong === 0 && missed === 0) {
      setFeedback({
        type: 'correct',
        message: `Perfect! ${correct}/${totalTargets} targets found`,
      });
    } else if (correct > missed) {
      setFeedback({
        type: 'partial',
        message: `${correct} correct, ${wrong} errors, ${missed} missed`,
      });
    } else {
      setFeedback({
        type: 'wrong',
        message: `${missed} targets missed, ${wrong} errors`,
      });
    }

    setTimeout(() => {
      setFeedback(null);

      if (currentSection + 1 >= TOTAL_SECTIONS) {
        finishGame(result, sectionScore);
      } else {
        setCurrentSection((prev) => prev + 1);
        setSelectedCells(new Set());
        setGrid(generateGrid());
        sectionSubmittedRef.current = false;
      }
    }, 2000);
  };

  const finishGame = (latestResult, latestSectionScore) => {
    setGameState(GAME_STATES.FINISHED);

    const timeSpent = Math.round((Date.now() - startTime.current) / 1000);
    const allResults = [...sectionResults, latestResult];

    const totalTargets = allResults.reduce((sum, result) => sum + result.totalTargets, 0);
    const totalCorrect = allResults.reduce((sum, result) => sum + result.correct, 0);
    const finalScore = score + latestSectionScore;
    const maxScore = totalTargets * 10;
    const accuracy = totalTargets > 0 ? (totalCorrect / totalTargets) * 100 : 0;

    onComplete({
      score: finalScore,
      maxScore,
      accuracy,
      timeSpent,
      sections: allResults,
    });
  };

  // Keep the ref pointing at the latest closure for the timer auto-submit.
  submitSectionRef.current = submitSection;

  const startGame = () => {
    setGameState(GAME_STATES.PLAYING);
    setScore(0);
    setCurrentSection(0);
    setTimeLeft(config.timePerSection);
    setSectionResults([]);
    setSelectedCells(new Set());
    setGrid([]);
    setFeedback(null);
    sectionSubmittedRef.current = false;
    startTime.current = Date.now();
  };

  if (gameState === GAME_STATES.READY) {
    return (
      <DeviceModeSelector
        onSelect={(mode) => {
          setDeviceMode(mode);
          setGameState(GAME_STATES.DEVICE_SELECT);
        }}
      />
    );
  }

  if (gameState === GAME_STATES.DEVICE_SELECT) {
    return (
      <Card className="border-slate-200">
        <CardContent className="pt-6 sm:pt-8">
          <div className="text-center mb-4 sm:mb-6">
            <div className="w-16 h-16 sm:w-20 sm:h-20 mx-auto mb-4 bg-[#0B1D51]/5 rounded-full flex items-center justify-center">
              <Target className="w-8 h-8 sm:w-10 sm:h-10 text-[#0B1D51]" />
            </div>

            <h2 className="font-heading text-xl sm:text-2xl font-bold text-[#0B1D51] mb-2">
              Concentration Grid
            </h2>

            <p className="text-sm text-[#64748B]">
              Level {level} - Selective Attention
            </p>

            <p className="text-xs text-[#FF9F1C] font-medium mt-2">
              Develops skills to improve performance on the Group Bourdon Test
            </p>
          </div>

          <div className="bg-[#F8FAFC] rounded-sm p-3 sm:p-4 mb-4 sm:mb-6">
            <h3 className="font-semibold text-[#0B1D51] mb-3 text-sm sm:text-base">
              How to Play:
            </h3>

            <ol className="space-y-2 text-xs sm:text-sm text-[#64748B]">
              <li className="flex gap-2">
                <span className="font-mono text-[#0B1D51]">1.</span>
                A grid shows groups of dots.
              </li>

              <li className="flex gap-2">
                <span className="font-mono text-[#0B1D51]">2.</span>
                <span>
                  Tap/click <strong>ONLY</strong> cells with exactly{' '}
                  <strong className="text-[#FF9F1C]">4 dots</strong>.
                </span>
              </li>

              <li className="flex gap-2">
                <span className="font-mono text-[#0B1D51]">3.</span>
                Ignore all other groups.
              </li>

              <li className="flex gap-2">
                <span className="font-mono text-[#0B1D51]">4.</span>
                Dot positions and pattern rotation vary, so count carefully.
              </li>

              <li className="flex gap-2">
                <span className="font-mono text-[#0B1D51]">5.</span>
                Press "Submit Section" when ready or when time runs out.
              </li>
            </ol>
          </div>

          {/* Visual example of dot patterns */}
          <div className="mb-4 sm:mb-6">
            <p className="text-sm text-[#64748B] mb-3 text-center">
              Pattern Examples:
            </p>

            <div className="flex justify-center gap-3 sm:gap-4">
              {[1, 2, 3, 4, 5].map((count) => (
                <div
                  key={count}
                  className={`w-12 h-12 sm:w-14 sm:h-14 rounded border-2 flex items-center justify-center text-[#0B1D51] ${
                    count === 4
                      ? 'border-[#FF9F1C] bg-[#FF9F1C]/10'
                      : 'border-slate-200'
                  }`}
                >
                  <DotPattern count={count} seed={count * 100} />
                </div>
              ))}
            </div>

            <div className="flex justify-center gap-3 sm:gap-4 mt-2">
              {[1, 2, 3, 4, 5].map((count) => (
                <div key={count} className="w-12 sm:w-14 text-center">
                  <p
                    className={`text-xs ${
                      count === 4
                        ? 'text-[#FF9F1C] font-bold'
                        : 'text-[#64748B]'
                    }`}
                  >
                    {count === 4 ? 'TARGET' : 'Ignore'}
                  </p>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-sm p-3 mb-4 sm:mb-6">
            <p className="text-xs sm:text-sm text-amber-800">
              <strong>RIS-3751-TOM:</strong> This exercise develops skills assessed by
              Group Bourdon-style cancellation tasks, measuring sustained concentration
              and selective attention to target stimuli while ignoring similar
              distractors.
            </p>
          </div>

          <Button
            onClick={startGame}
            size="lg"
            className="w-full bg-[#0B1D51] hover:bg-[#0B1D51]/90"
            data-testid="start-game-btn"
          >
            <Play className="w-5 h-5 mr-2" />
            Start Test ({TOTAL_SECTIONS} sections)
          </Button>
        </CardContent>
      </Card>
    );
  }

  const cols = deviceMode === 'mobile' ? 6 : 10;

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;

    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs text-[#64748B]">Score</p>
          <p className="font-mono text-xl font-bold text-[#0B1D51]">{score}</p>
        </div>

        <Badge variant="outline" className="text-[#64748B]">
          Section {currentSection + 1}/{TOTAL_SECTIONS}
        </Badge>

        <div className="text-right">
          <p className="text-xs text-[#64748B]">Time</p>
          <p
            className={`font-mono text-xl font-bold ${
              timeLeft < 30 ? 'text-[#EF4444]' : 'text-[#0B1D51]'
            }`}
          >
            {formatTime(timeLeft)}
          </p>
        </div>
      </div>

      {/* Progress */}
      <Progress value={(currentSection / TOTAL_SECTIONS) * 100} className="h-1" />

      {/* Instructions reminder */}
      <div className="bg-white border rounded-sm p-2 text-center">
        <p className="text-sm text-[#64748B]">
          Select all cells with exactly{' '}
          <strong className="text-[#FF9F1C]">4 dots</strong>
        </p>
      </div>

      {/* Grid */}
      <Card className="border-slate-200">
        <CardContent className="py-3 sm:py-4">
          <div
            className="grid gap-1 sm:gap-2"
            style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}
          >
            {grid.map((cell) => (
              <motion.button
                key={cell.id}
                whileTap={{ scale: 0.95 }}
                onClick={() => handleCellClick(cell)}
                className={`aspect-square rounded border-2 flex items-center justify-center transition-all ${
                  selectedCells.has(cell.id)
                    ? 'border-[#FF9F1C] bg-[#FF9F1C]/20 text-[#0B1D51]'
                    : 'border-slate-200 bg-white text-[#0B1D51] hover:border-slate-300'
                }`}
                data-testid={`cell-${cell.id}`}
              >
                <DotPattern
                  count={cell.dotCount}
                  size={deviceMode === 'mobile' ? 'small' : 'normal'}
                  seed={cell.seed}
                />
              </motion.button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Submit button */}
      <Button
        onClick={submitSection}
        size="lg"
        className="w-full bg-[#2CB67D] hover:bg-[#2CB67D]/90"
        data-testid="submit-section-btn"
      >
        <CheckCircle className="w-5 h-5 mr-2" />
        Submit Section {currentSection + 1}

        {currentSection + 1 < TOTAL_SECTIONS && (
          <ArrowRight className="w-4 h-4 ml-2" />
        )}
      </Button>

      {/* Selected count */}
      <p className="text-center text-sm text-[#64748B]">
        {selectedCells.size} cells selected
      </p>

      {/* Feedback overlay */}
      {feedback && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className={`fixed inset-x-4 bottom-24 p-4 rounded-sm text-center font-medium shadow-lg ${
            feedback.type === 'correct'
              ? 'bg-[#2CB67D] text-white'
              : feedback.type === 'partial'
                ? 'bg-[#FF9F1C] text-white'
                : 'bg-[#EF4444] text-white'
          }`}
        >
          {feedback.message}
        </motion.div>
      )}
    </div>
  );
};

export default SelectiveAttentionGame;